(() => {
  const TRACKED_PARAMETERS = [
    'utm_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_source_platform',
    'utm_marketing_tactic',
    'fbclid',
    'gclid',
    'ttclid',
    'tt_campaignid',
    'lp_version',
  ];

  const STORAGE_KEY = 'dg_paid_attribution_v1';
  const CART_SYNC_KEY = 'dg_paid_cart_sync_v1';
  const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  // A new visitor can submit before the first cart update returns. Reuse the
  // same request, so parallel requests cannot create competing empty carts.
  let cartSyncInFlight = null;

  const readStoredAttribution = () => {
    try {
      const sessionValue = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
      if (Object.keys(sessionValue).length) return sessionValue;
    } catch {
      // Fall through to the durable copy.
    }
    try {
      const localValue = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
      const storedAt = Date.parse(localValue.stored_at || '');
      if (!storedAt || Date.now() - storedAt > ATTRIBUTION_MAX_AGE_MS) {
        window.localStorage.removeItem(STORAGE_KEY);
        return {};
      }
      return localValue;
    } catch {
      return {};
    }
  };

  const storeAttribution = (captured) => {
    const durable = { ...captured, stored_at: captured.stored_at || new Date().toISOString() };
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(durable));
    } catch {
      // Continue with durable storage when session storage is unavailable.
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(durable));
    } catch {
      // Tracking remains functional when durable storage is unavailable.
    }
    return durable;
  };

  const captureAttribution = (currentUrl) => {
    const stored = readStoredAttribution();
    const captured = { ...stored };
    let hasCurrentAttribution = false;

    for (const key of TRACKED_PARAMETERS) {
      const value = currentUrl.searchParams.get(key);
      if (!value) continue;
      captured[key] = value;
      if (key !== 'lp_version') hasCurrentAttribution = true;
    }
    if (hasCurrentAttribution) captured.landing_path = `${currentUrl.pathname}${currentUrl.search}`;

    if (hasCurrentAttribution) captured.stored_at = new Date().toISOString();
    return storeAttribution(captured);
  };

  const syncCartAttribution = (captured) => {
    if (cartSyncInFlight) return cartSyncInFlight;
    if (!captured.utm_source || !captured.utm_medium) {
      document.documentElement.dataset.dgPaidCartSync = 'not_applicable';
      return;
    }
    const attributes = {
      dg_utm_source__: captured.utm_source,
      dg_utm_medium__: captured.utm_medium,
      dg_utm_campaign__: captured.utm_campaign || '',
      dg_utm_content__: captured.utm_content || '',
      dg_utm_term__: captured.utm_term || '',
      dg_lp_version__: captured.lp_version || '',
      dg_landing_path__: captured.landing_path || window.location.pathname,
      dg_fbclid__: captured.fbclid || '',
      dg_gclid__: captured.gclid || '',
      dg_ttclid__: captured.ttclid || '',
    };
    const signature = JSON.stringify(attributes);
    try {
      if (window.sessionStorage.getItem(CART_SYNC_KEY) === signature) {
        document.documentElement.dataset.dgPaidCartSync = 'cached';
        return;
      }
    } catch {
      // Continue with the cart update when storage is unavailable.
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    cartSyncInFlight = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
      credentials: 'same-origin',
      keepalive: true,
      signal: controller.signal,
    }).then((response) => {
      if (!response.ok) throw new Error(`cart attribution sync failed (${response.status})`);
      try {
        window.sessionStorage.setItem(CART_SYNC_KEY, signature);
      } catch {
        // No action needed; Shopify already received the attribution.
      }
      document.documentElement.dataset.dgPaidCartSync = 'ok';
    }).catch(() => {
      document.documentElement.dataset.dgPaidCartSync = 'error';
      // Do not interrupt the buying journey if Shopify's cart endpoint is unavailable.
    }).finally(() => {
      window.clearTimeout(timeout);
      cartSyncInFlight = null;
    });
    return cartSyncInFlight;
  };

  const eventNameFor = (root) => {
    const key = root.dataset.templateKey || '';
    if (key.includes('offer')) return 'dg_lp_offer_add';
    if (key.includes('routine')) return 'dg_lp_routine_select';
    if (key.includes('beard_balm')) return 'dg_lp_balm_add';
    return 'dg_lp_product_select';
  };

  const publish = (root, action, target) => {
    const detail = {
      lp_version: root.dataset.lpVersion || 'ml_2026_07_v1',
      landing_page_id: root.dataset.landingPageId || null,
      template_key: root.dataset.templateKey || null,
      language: root.dataset.language || document.documentElement.lang || null,
      action,
      target: target?.getAttribute('href') || target?.dataset.lpTarget || target?.dataset.variantId || null,
    };
    const event = action === 'supporting' ? 'dg_lp_supporting_action' : eventNameFor(root);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...detail });
    try {
      window.Shopify?.analytics?.publish?.(event, detail);
    } catch {
      // Keep the storefront interaction intact if Customer Events is unavailable.
    }
    root.dataset.lpLastEvent = event;
    root.dataset.lpLastAction = action;
    root.dataset.lpLastVersion = detail.lp_version;
    document.dispatchEvent(new CustomEvent(event, { detail }));
  };

  const decorateInternalLinks = (root, currentUrl, captured) => {
    const values = new Map();
    for (const key of TRACKED_PARAMETERS) {
      const value = currentUrl.searchParams.get(key) || captured[key];
      if (value) values.set(key, value);
    }
    if (!values.has('lp_version')) {
      values.set('lp_version', root.dataset.lpVersion || 'ml_2026_07_v1');
    }

    for (const link of root.querySelectorAll('a[href], [data-lp-target]')) {
      const isAnchor = link.matches('a[href]');
      const href = isAnchor ? link.getAttribute('href') : link.dataset.lpTarget;
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) continue;
      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        continue;
      }
      if (url.origin !== window.location.origin) continue;
      for (const [key, value] of values) {
        if (!url.searchParams.has(key)) url.searchParams.set(key, value);
      }
      const decorated = `${url.pathname}${url.search}${url.hash}`;
      if (isAnchor) link.href = decorated;
      else link.dataset.lpTarget = decorated;
    }
  };

  const applyRouteVariant = (root, currentUrl) => {
    const path = currentUrl.pathname.replace(/\/+$/, '');
    const version = currentUrl.searchParams.get('lp_version');
    const variants = {
      routine_order_2026_08_v1: {
        path: '/ar/pages/saudi-routine-starter-ar',
        eyebrow: 'رتّب روتينك',
        title: 'بعد الشاور: نعّم، غذّي، ورتّب',
        subheadline: 'منعم اللحية أثناء الاستحمام، زيت اللحية بعد الشطف والتجفيف، والفرشاة كآخر خطوة. ابدأ بالخطوة اللي ناقصتك اليوم.',
        cta: 'ابدأ بالخطوة المناسبة',
      },
      softener_use_2026_08_v1: {
        path: '/ar/pages/saudi-best-sellers-ar-softener',
        eyebrow: 'أثناء الاستحمام',
        title: 'منعم اللحية يُستخدم أثناء الاستحمام—ثم يُشطف',
        subheadline: 'ضع كمية مناسبة على راحة اليد، ووزّعها على اللحية والشارب. اتركها من دقيقتين إلى خمس دقائق، ثم اشطفها جيدًا.',
        cta: 'اختار منعم اللحية',
      },
    };
    const variant = variants[version];
    if (!variant || path !== variant.path) return;

    const eyebrow = root.querySelector('.paid-landing-page__hero-content .paid-landing-page__eyebrow');
    const title = root.querySelector('.paid-landing-page__title');
    const subheadline = root.querySelector('.paid-landing-page__subheadline');
    const primary = root.querySelector('.paid-landing-page__hero-content [data-lp-action="primary"]');
    if (eyebrow) eyebrow.textContent = variant.eyebrow;
    if (title) title.textContent = variant.title;
    if (subheadline) subheadline.textContent = variant.subheadline;
    if (primary) {
      primary.textContent = variant.cta;
      primary.setAttribute('href', '#lp-products');
    }

    document.body.classList.add('dg-paid-route-test');
    const sticky = document.createElement('a');
    sticky.className = 'dg-paid-route-sticky';
    sticky.href = '#lp-products';
    sticky.dataset.lpAction = 'primary';
    sticky.textContent = variant.cta;
    root.appendChild(sticky);
  };

  const initialize = () => {
    const currentUrl = new URL(window.location.href);
    const roots = document.querySelectorAll('.paid-landing-page, .routine-gift-landing, .beard-balm-landing');
    const captured = captureAttribution(currentUrl);
    // A new landing owns its version; a downstream PDP retains the source version.
    if (roots.length === 1) {
      captured.lp_version = currentUrl.searchParams.get('lp_version') || roots[0].dataset.lpVersion || 'ml_2026_07_v1';
      storeAttribution(captured);
    }
    syncCartAttribution(captured);

    for (const root of roots) {
      if (root.dataset.lpAttributionReady === 'true') continue;
      root.dataset.lpAttributionReady = 'true';
      root.dataset.lpVersion = currentUrl.searchParams.get('lp_version') || root.dataset.lpVersion || 'ml_2026_07_v1';
      applyRouteVariant(root, currentUrl);
      decorateInternalLinks(root, currentUrl, captured);

      root.addEventListener('click', (event) => {
        const source = event.target instanceof Element ? event.target : event.target?.parentElement;
        const target = source?.closest('[data-lp-action], [data-routine-submit], [data-bb-submit], a[href]');
        if (!target || !root.contains(target)) return;
        syncCartAttribution(captured);
        const explicitAction = target.dataset.lpAction;
        if (explicitAction) {
          publish(root, explicitAction, target);
          return;
        }
        if (target.matches('[data-routine-submit], [data-bb-submit]')) {
          publish(root, 'primary', target);
          return;
        }
        const href = target.getAttribute('href') || '';
        publish(root, href.startsWith('#') ? 'supporting' : 'product', target);
      }, { capture: true });

      root.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('[data-lp-quick-form]')) return;
        event.preventDefault();
        if (root.dataset.lpSubmitting === 'true' || form.dataset.lpSubmitting === 'true') return;
        root.dataset.lpSubmitting = 'true';
        form.dataset.lpSubmitting = 'true';
        root.querySelectorAll('[data-lp-quick-form] [type="submit"]').forEach((item) => { item.disabled = true; });
        const button = form.querySelector('[type="submit"]');
        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          const label = button.querySelector('span');
          if (label && button.dataset.loadingLabel) {
            button.dataset.originalLabel = label.textContent;
            label.textContent = button.dataset.loadingLabel;
          }
        }
        // Once attribution is resolved (or timed out), use Shopify's regular
        // form navigation, which exposes the cart or Shopify's error response.
        Promise.resolve(syncCartAttribution(captured)).finally(() => HTMLFormElement.prototype.submit.call(form));
      });

      // Back navigation may restore this document from the browser cache.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        delete root.dataset.lpSubmitting;
        root.querySelectorAll('[data-lp-quick-form]').forEach((form) => {
          delete form.dataset.lpSubmitting;
          const button = form.querySelector('[type="submit"]');
          if (!button) return;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          const label = button.querySelector('span');
          if (label && button.dataset.originalLabel) label.textContent = button.dataset.originalLabel;
        });
      });

      // Avoid a fast click escaping attribution before this deferred script is ready.
      root.querySelectorAll('[data-lp-quick-form] [type="submit"]').forEach((button) => { button.disabled = false; });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
